import { Flex, Grid, Switch, Text } from '@radix-ui/themes';
import React, { useState, useEffect } from 'react';
import SpecialRoleRoomCard from '../components/ui-components/SpecialRoleRoomCard';
import SOSButton from '../components/ui-components/SOSButton';
import ResetAlertPopup from '../components/ui-components/ResetAlertPopup';
import axiosInstance from '../config/axiosConfig';
import { useAuth } from '../context/AuthContext';
import { getApiDateTimeMillis, parseApiDate } from '../utils/helpers';

const SpecialRoleMyPatientsPage = () => {
  const [roomsData, setRoomsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bedAlarms, setBedAlarms] = useState({}); // Structure: { bedId: alarm }
  const [resetAlert, setResetAlert] = useState(null); // For showing reset popup
  const [currentPlayingAudio, setCurrentPlayingAudio] = useState(null);
  const [currentAudioUrl, setCurrentAudioUrl] = useState(null);
  const [showOnlyRoomsWithPatients, setShowOnlyRoomsWithPatients] = useState(true);
  
  const roleId = localStorage.getItem('roleId');
  const userId = parseInt(localStorage.getItem('userId'));
  const { activeAlerts, clearAllAlerts } = useAuth();
  const alarmSettings = JSON.parse(localStorage.getItem('alarmSettings')) || [];

  // Role-based configuration
  const getRoleConfig = () => {
    switch (roleId) {
      case '5': return { title: 'Security Tasks', masterAlarmId: 6 };
      case '6': return { title: 'Housekeeping Tasks', masterAlarmId: 7 };
      case '7': return { title: 'F&B Tasks', masterAlarmId: 5 };
      default: return { title: 'Tasks', masterAlarmId: null };
    }
  };

  const roleConfig = getRoleConfig();

  // Helper function to convert camelCase to regular words
  const convertCamelCaseToWords = (text) => {
    return text.replace(/([A-Z])/g, ' $1').trim();
  };

  // Helper function to send notification to backend
  const sendNotificationToBackend = async (bedId, roomId, notificationText, time, type) => {
    try {
      const notificationModel = {
        BedId: bedId,
        RoomId: roomId,
        NotificationText: notificationText,
        Time: time,
        Type: type
      };

      console.log('Sending notification to backend:', notificationModel);

      const response = await axiosInstance.post('/Notification/add-notification', notificationModel);
      
      if (response.data && response.data.result) {
        console.log('Notification sent successfully');
        // Refresh notifications immediately after success
        await refreshNotifications();
      } else {
        console.error('Failed to send notification:', response.data?.errorResponse?.message || 'Unknown error');
      }
    } catch (error) {
      console.error('Error sending notification:', error);
    }
  };

  // Helper function to refresh notifications in header
  const refreshNotifications = async () => {
    try {
      // This will trigger the header to refresh its notifications
      // We can dispatch a custom event that the header listens to
      window.dispatchEvent(new CustomEvent('refreshNotifications'));
    } catch (error) {
      console.error('Error refreshing notifications:', error);
    }
  };

  const fetchBeds = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/Bed/get-beds', {
        params: { token: null },
      });
      const bedList = response.data.result?.bedList || [];
      setRoomsData(bedList);
    } catch (err) {
      setError('Failed to load bed data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBeds();
    
    // Cleanup function to stop audio when component unmounts
    return () => {
      console.log('SpecialRoleMyPatientsPage unmounting, cleaning up audio...');
      stopCurrentAudio();
    };
  }, []);

  // Process SignalR alerts
  useEffect(() => {
    console.log('SpecialRoleMyPatientsPage - SignalR activeAlerts updated:', activeAlerts);
    
    if (activeAlerts.length > 0) {
      // Sort alerts by timestamp (oldest first)
      const sortedAlerts = [...activeAlerts].sort((a, b) => {
        const timeA = getApiDateTimeMillis(a.timestamp || a["0"]?.timestamp || 0);
        const timeB = getApiDateTimeMillis(b.timestamp || b["0"]?.timestamp || 0);
        return timeA - timeB;
      });
      
      console.log('SpecialRoleMyPatientsPage - Processing alerts:');
      
      // Process each alert (excluding SOS)
      sortedAlerts.forEach((alert, index) => {
        const alertTime = alert.timestamp || alert["0"]?.timestamp;
        const alertType = alert["0"]?.alertType || alert.alertType;
        console.log(`${index + 1}. Alert time: ${alertTime}, Type: ${alertType}`);
        
        if (alertType !== 'SOS') {
          processSignalRAlert(alert);
        }
      });
      
      clearProcessedAlerts();
    }
  }, [activeAlerts]);

  const processSignalRAlert = (alert) => {
    console.log('SpecialRoleMyPatientsPage - Processing SignalR alert:', alert);
    
    if (!alert || typeof alert !== 'object') {
      console.warn('Invalid alert received:', alert);
      return;
    }

    // Check if alert is wrapped
    let actualAlert = alert;
    if (alert["0"] && typeof alert["0"] === 'object') {
      actualAlert = alert["0"];
    }

    const { alertType, payload } = actualAlert;
    
    console.log(`🔍 Processing alert type: ${alertType}, timestamp: ${actualAlert.timestamp}`);
    
    if (alertType === 'SOS') {
      console.log('🆘 Skipping SOS alert - handled by AppLayout');
      return;
    }

    if (!payload || typeof payload !== 'object') {
      console.warn('Alert missing payload:', actualAlert);
      return;
    }

    const { bedId, masterAlarmId, roomId } = payload;
    
    if (bedId === undefined || bedId === null) {
      console.warn('Alert missing bedId in payload:', actualAlert);
      return;
    }

    console.log('✅ Valid alert - AlertType:', alertType, 'BedId:', bedId, 'MasterAlarmId:', masterAlarmId);

    // Send notification for all alert types
    const alertTimestamp = parseApiDate(actualAlert.timestamp);
    const timestamp = alertTimestamp ? alertTimestamp.toISOString() : new Date().toISOString();

    if (alertType === 'Confirmation') {
      console.log('📝 Processing CONFIRMATION alert for bedId:', bedId);
      // Confirmation is always type 1
      sendNotificationToBackend(bedId, roomId, `${convertCamelCaseToWords(alertType)} alert received on`, timestamp, 1);
      handleConfirmation(bedId, actualAlert);
    } else {
      // Check if this is an alarm for our role
      if (masterAlarmId === roleConfig.masterAlarmId) {
        console.log(`🚨 Processing ALARM for our role (${roleConfig.title}):`, alertType, 'bedId:', bedId);
        // Special role alarms are type 3
        sendNotificationToBackend(bedId, roomId, `${convertCamelCaseToWords(alertType)} alert received on`, timestamp, 3);
        handleNewAlarm(bedId, masterAlarmId, payload, actualAlert);
      } else {
        console.log(`⚠️ Ignoring alarm for different role. Expected masterAlarmId: ${roleConfig.masterAlarmId}, Got: ${masterAlarmId}`);
      }
    }
  };

  const handleNewAlarm = (bedId, masterAlarmId, payload, alert) => {
    try {
      const alarmConfig = alarmSettings.find(alarm => alarm.masterAlarmId === masterAlarmId);
      if (!alarmConfig) {
        console.warn('No alarm configuration found for masterAlarmId:', masterAlarmId);
        return;
      }

      const newAlarm = {
        bedId: bedId.toString(),
        masterAlarmId,
        alarmName: alarmConfig.alarmName,
        color: alarmConfig.color,
        tunePath: alarmConfig.tunePath,
        timestamp: Date.now(),
        payload,
        alert // Store original alert for potential reset popup
      };

      console.log('Creating new alarm for special role:', newAlarm);

      setBedAlarms(prev => {
        const newBedAlarms = {
          ...prev,
          [bedId]: newAlarm
        };

        console.log('Updated bed alarms:', newBedAlarms);
        playAlarmSound(newAlarm);
        return newBedAlarms;
      });
    } catch (error) {
      console.error('Error in handleNewAlarm:', error);
    }
  };

  const handleConfirmation = (bedId, confirmationAlert) => {
    try {
      console.log('📝 Handling confirmation for bedId:', bedId);
      
      // Check if this confirmation is for the current user
      const alertUserId = confirmationAlert.payload?.userId;
      
      console.log('🔍 Confirmation alert userId check:', {
        alertUserId,
        userId,
        match: alertUserId === userId
      });

      if (alertUserId === userId) {
        console.log('✅ Confirmation alert is for current user, showing reset popup');
        setResetAlert(confirmationAlert);
      } else {
        console.log('❌ Confirmation alert is not for current user, ignoring');
      }

      // Clear the alarm from the bed regardless of user match
      setBedAlarms(prev => {
        const newBedAlarms = { ...prev };
        delete newBedAlarms[bedId];
        console.log('Cleared alarm for bedId due to confirmation:', bedId);
        stopCurrentAudio();
        return newBedAlarms;
      });

    } catch (error) {
      console.error('Error in handleConfirmation:', error);
    }
  };

  const playAlarmSound = (alarm) => {
    stopCurrentAudio();

    const playAudio = async () => {
      try {
        const dbRequest = indexedDB.open('AlarmAudioDB', 1);
        
        dbRequest.onsuccess = (event) => {
          const db = event.target.result;
          const transaction = db.transaction(['audioFiles'], 'readonly');
          const store = transaction.objectStore('audioFiles');
          const request = store.get(alarm.tunePath); 
          
          request.onsuccess = (e) => {
            const audioBlob = e.target.result;
            if (audioBlob) {
              const audioUrl = URL.createObjectURL(audioBlob);
              console.log('Created new audio URL:', audioUrl);
              
              const audio = document.createElement('audio');
              audio.src = audioUrl;
              audio.loop = true;
              audio.preload = 'auto';
              audio.style.display = 'none';
              
              audio.setAttribute('data-alarm-audio', 'true');
              audio.setAttribute('data-bed-id', alarm.bedId);
              audio.setAttribute('data-alarm-id', alarm.masterAlarmId);
              
              document.body.appendChild(audio);
              
              setCurrentPlayingAudio(audio);
              setCurrentAudioUrl(audioUrl);
              
              audio.addEventListener('ended', () => {
                console.log('Audio ended naturally');
                cleanupAudio(audio, audioUrl);
              });
              
              audio.addEventListener('error', (error) => {
                console.error('Audio error occurred:', error);
                cleanupAudio(audio, audioUrl);
              });
              
              const playPromise = audio.play();
              
              if (playPromise !== undefined) {
                playPromise.then(() => {
                  console.log('Audio playing successfully for alarm:', alarm.alarmName);
                }).catch(err => {
                  console.error('Audio playback error:', err);
                  cleanupAudio(audio, audioUrl);
                });
              }
              
            } else {
              console.warn(`No audio found for ${alarm.tunePath}`);
            }
          };
          
          request.onerror = (error) => {
            console.error('Error retrieving audio from IndexedDB:', error);
          };
        };
        
        dbRequest.onerror = (error) => {
          console.error('Error opening IndexedDB:', error);
        };
      } catch (error) {
        console.error('Error playing alarm sound:', error);
      }
    };

    playAudio();
  };

  const cleanupAudio = (audio, audioUrl) => {
    try {
      if (audio && audio.parentNode) {
        audio.parentNode.removeChild(audio);
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      setCurrentPlayingAudio(null);
      setCurrentAudioUrl(null);
    } catch (error) {
      console.error('Error in cleanupAudio:', error);
    }
  };

  const stopCurrentAudio = () => {
    console.log('Attempting to stop current audio...');
    
    if (currentPlayingAudio) {
      try {
        console.log('Stopping audio object...');
        currentPlayingAudio.pause();
        currentPlayingAudio.currentTime = 0;
        cleanupAudio(currentPlayingAudio, currentAudioUrl);
      } catch (error) {
        console.error('Error stopping audio object:', error);
      }
    }
    
    setCurrentPlayingAudio(null);
    setCurrentAudioUrl(null);
    console.log('Audio cleanup completed');
  };

  const handleManualConfirm = async (bedId, alarmId) => {
    try {
      const alarmManagementModel = {
        BedId: parseInt(bedId),
        Id: alarmId // Include the alarm id from the payload
      };

      console.log('Calling confirm-alarm API with:', alarmManagementModel);

      const response = await axiosInstance.post('/AlarmManagement/confirm-gateway-alarm', alarmManagementModel);
      
      if (response.data && response.data.result) {
        console.log('Alarm confirmed successfully in backend');
      } else {
        console.error('Backend confirm alarm failed:', response.data?.errorResponse?.message || 'Unknown error');
        return;
      }

      setBedAlarms(prev => {
        const newBedAlarms = { ...prev };
        delete newBedAlarms[bedId];
        console.log('Frontend alarm confirmed for bedId:', bedId);
        stopCurrentAudio();
        return newBedAlarms;
      });

    } catch (error) {
      console.error('Error confirming alarm:', error);
    }
  };

  const handleManualCancel = (bedId) => {
    console.log('Cancelling alarm for bedId:', bedId);
    
    setBedAlarms(prev => {
      const newBedAlarms = { ...prev };
      delete newBedAlarms[bedId];
      console.log('Alarm cancelled for bedId:', bedId);
      stopCurrentAudio();
      return newBedAlarms;
    });
  };

  const clearProcessedAlerts = () => {
    console.log('🧹 SpecialRoleMyPatientsPage - Clearing all processed alerts from activeAlerts');
    clearAllAlerts();
  };

  const handleDismissReset = () => {
    console.log('Dismissing reset alert popup');
    setResetAlert(null);
  };

  // Sort rooms - beds with alarms first, then by timestamp
  const filteredRooms = roomsData.filter((bed) => {
    if (showOnlyRoomsWithPatients && !bed.patientId) {
      return false;
    }
    return true;
  });

  const sortedRooms = [...filteredRooms].sort((a, b) => {
    const hasAlarmA = !!bedAlarms[a.id];
    const hasAlarmB = !!bedAlarms[b.id];
    
    if (hasAlarmA && hasAlarmB) {
      // Both have alarms - sort by timestamp (oldest first)
      return bedAlarms[a.id].timestamp - bedAlarms[b.id].timestamp;
    }
    
    if (hasAlarmA) return -1; // A has alarm, comes first
    if (hasAlarmB) return 1;  // B has alarm, comes first
    
    return 0; // Neither has alarms, maintain original order
  });

  if (loading) {
    return <Flex justify="center">Loading...</Flex>;
  }

  if (error) {
    return <Flex justify="center">{error}</Flex>;
  }

  return (
    <>
      {/* Reset Alert Popup */}
      <ResetAlertPopup
        resetAlert={resetAlert}
        onDismiss={handleDismissReset}
        title="Add Remarks"
      />

      <Flex direction="column" gap="3">
        <div className="sticky -top-7 z-10 bg-[#e8e8e8] py-3 -mt-7 pt-7 -mx-4 px-4 mb-3 shadow-sm border-b border-gray-300">
          <Flex justify="between" align="center" gap="3" wrap="wrap">
            <Flex align="center" gap="5" wrap="wrap">
              <Text as="label" size="2">
                <Flex gap="2" align="center">
                  <Switch
                    checked={showOnlyRoomsWithPatients}
                    onCheckedChange={setShowOnlyRoomsWithPatients}
                    size="1"
                  />
                  Only occupied rooms
                </Flex>
              </Text>
            </Flex>
            
            <Flex align="center" gap="3">
              <SOSButton />
            </Flex>
          </Flex>
        </div>
        
        <Grid 
          columns={{ initial: '1', xs: '2', sm: '3', md: '4', lg: '6' }} 
          gap="3" 
          width="100%"
        >
          {sortedRooms.map((bed) => (
            <SpecialRoleRoomCard 
              key={bed.id} 
              bedData={bed} 
              roleId={roleId}
              alarm={bedAlarms[bed.id] || null}
              onConfirmAlarm={() => handleManualConfirm(bed.id, bedAlarms[bed.id]?.payload?.id)}
              onCancelAlarm={() => handleManualCancel(bed.id)}
            />
          ))}
        </Grid>
      </Flex>
    </>
  );
};

export default SpecialRoleMyPatientsPage;
